import { useUserAuthStore } from 'src/stores/user/auth'
import { AUTOLOGIN_APP_TOKEN_TIMEOUT, AUTOLOGIN_ENDPOINTS } from 'src/common/autologin/constants'
import { getFingerprint, getLocale } from 'src/common/functions'
import Platform from 'src/common/platform'
import { tokenService } from 'src/common/token-service'
import { userService } from 'src/common/user-service'
import { getConfig } from 'src/configs'
import { getConsumerLocale } from 'src/core/locale/runtime'

const config = getConfig()
let controller

const SKIP_LOGOUT_ON_401 = [
  '/authservice/api/v1/user/signin/',
  AUTOLOGIN_ENDPOINTS.appToken,
  AUTOLOGIN_ENDPOINTS.validate,
]

export class API {
  axios = void 0

  constructor(axios) {
    this.axios = axios

    if (Platform.is.mobile) {
      this.axios.defaults.headers.common.CASISMOBILE = 1
    }

    this.addRequestInterceptor(axios)
    this.addResponseInterceptor(axios)
  }

  addRequestInterceptor = (axios) => {
    axios.interceptors.request.use(
      async (config) => {
        let tokenFromCookies = tokenService.getToken()
        let tokenFromStore = useUserAuthStore().getToken
        const token = tokenFromStore || tokenFromCookies

        if (process.env.PLATFORM !== 'olimpbet' && typeof token === 'string' && token) {
          config.headers.Authorization = `JWT ${token}`
        }

        config.headers['Accept-Language'] = getConsumerLocale({
          consumer: 'rest',
          profileLocale: useUserAuthStore().getUserLanguage,
          getFallbackLocale: getLocale,
        })
        config.headers['client-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone

        if (window.__E2E__) {
          config.headers['x-e2e-test'] = 1
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  addResponseInterceptor = (axios) => {
    axios.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        if (!originalRequest) {
          return Promise.reject(error)
        }

        if (originalRequest.url === 'token-refresh/' || originalRequest._retry) {
          return Promise.reject(error)
        }

        const tokenIsExpired =
          (error.response?.status === 401 && !SKIP_LOGOUT_ON_401.includes(error.config.url)) ||
          (error.response?.data?.non_field_errors &&
            error.response?.data?.non_field_errors[0] === 'Signature has expired.')

        if (tokenIsExpired) {
          useUserAuthStore().removeToken()
          await userService.logout()

          const ENDPOINTS_USER = [/signin\//, /signin\/bk\/.*/]

          if (ENDPOINTS_USER.some((rx) => rx.test(originalRequest?.url))) {
            return Promise.reject(error)
          }

          originalRequest._retry = true
          delete originalRequest.headers.Authorization
          return this.axios(originalRequest)
        }

        return Promise.reject(error)
      },
    )
  }

  // ------------------------------------------- COMMON ---------------------------------------------------------------
  common = {
    underDev: async () => {
      return this.axios.get('underdev/', {
        cache: {
          ignoreCache: true,
          readOnError: false,
        },
      })
    },
    restrictions: async () => {
      return this.axios.get('g/restrictions/', {
        cache: {
          ignoreCache: true,
          readOnError: false,
        },
      })
    },
  }
  // ------------------------------------------- CASHBOX --------------------------------------------------------------
  cashbox = {
    getTopupBankMethods: async () => this.axios.get('bank/get/topup/methods/'),
    getTerminalToPup: async (params) => this.axios.post('bank/get/topup/terminal/', params),
    getWithdrawBankMethods: async () => this.axios.get('bank/get/withdraw/methods/'),
    getWithdrawTerminal: async (data) => this.axios.post('bank/get/withdraw/terminal/', data),
    getSmsToTransferToBK: async () => this.axios.get('bank/bk/withdraw/sms/'),
    bankCheck: async (params) => this.axios.post('bank/bk/check/', params),

    bankBKWithdraw: async (params) => this.axios.post('bank/bk/withdraw/', params),
    bankBKTopup: async (params) => this.axios.post('bank/bk/topup/', params),
  }
  // ------------------------------------------- PROFILE --------------------------------------------------------------
  profile = {
    // --- Messages ---
    messagesList: async () => this.axios.get('user/messages/list/'),

    getCodeForSmsVerification: async () => this.axios.get('bank/bk/disable/sms/'),
    approveDisableSmsVerification: async (params) => this.axios.post('bank/bk/disable/sms/approve/', { params }),
    approveEnableSmsVerification: async () => this.axios.get('bank/bk/enable/sms/approve/'),
  }
  // ------------------------------------------- COMPETITION ----------------------------------------------------------
  competitions = {
    loadmore: async (url) => {
      return this.axios.get(`${url}`)
    },
  }
  // ------------------------------------------- BONUSES --------------------------------------------------------------
  bonuses = {
    chooseWelcomeBonus: async (payload) => {
      return this.axios.post('user/bonus/welcome/add/', payload)
    },
    getBonus: async (id) => {
      return this.axios.get(`bonus/${id}/info/`)
    },
    activateBonus: async (payload) => this.axios.post('user/bonus/activate/', payload),
  }
  // ------------------------------------------- AUTH -----------------------------------------------------------------
  auth = {
    signIn: async (data) => {
      const fingerPrint = await getFingerprint()

      return this.axios({
        url: '/authservice/api/v1/user/signin/',
        baseURL: '',
        data,
        method: 'post',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },
    refreshToken: async (payload) => {
      return this.axios.post('token-refresh/', payload, {
        headers: {
          Authorization: null,
        },
      })
    },

    signInTelegramMiniApp: async (initData) => {
      const fingerPrint = await getFingerprint()

      return this.axios({
        url: '/api/g/tg_miniapp/',
        baseURL: '',
        method: 'get',
        params: { initData },
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },

    // getSmsForRecoveryPass: async (payload) => {
    //   return this.axios.post('user/password/reset/getsmscode/', payload)
    // },
    sendPhoneToGetSms: async (data) => {
      return this.axios({
        url: '/authservice/api/v1/user/reset-password/sms/send-code/',
        method: 'post',
        data,
        baseURL: '',
      })
    },
    checkSMScode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/reset-password/sms/check-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    sendCode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/sms/send-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    checkCode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/sms/check-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    setPassword: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/set-password',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    socialRegistration: async (slug, payload) => {
      return this.axios({
        url: `/authservice/api/v1/user/${slug}/registration/`,
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    checkUserBySocial: async (socialName, payload) => {
      return this.axios({
        url: `/authservice/api/v1/user/${socialName}/check-account-exists/`,
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    confirmUserBySocial: async (socialName, payload) => {
      return this.axios({
        url: `/authservice/api/v1/user/${socialName}/socialaccount/socialaccount-confirm/`,
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    socialRegistrationOlymp: async (socialName, payload) => {
      return this.axios({
        url: `/authservice/api/v1/user/${socialName}/login-after-otp/`,
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    socialProvidersList: async () => {
      return this.axios({
        url: '/authservice/api/v1/socialnetwork/provider/list/',
        baseURL: '',
      })
    },
    registration: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/pre-registration/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    finallyRegistration: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/finally-registration/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    registrationOneStep: async (payload) => {
      const fingerPrint = await getFingerprint()
      return this.axios({
        url: '/authservice/api/v1/user/registration/one-step/',
        method: 'post',
        data: payload,
        baseURL: '',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },
    confirmRegistrationWithCode: async (payload) => {
      const fingerPrint = await getFingerprint()
      return this.axios({
        url: '/authservice/api/v1/user/registration/one-step/confirm/',
        method: 'post',
        data: payload,
        baseURL: '',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },
    resendRegistrationEmailCode: async (payload) => {
      const fingerPrint = await getFingerprint()
      return this.axios({
        url: '/authservice/api/v1/user/registration/one-step/resend/',
        method: 'post',
        data: payload,
        baseURL: '',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },
    getSession: async () => {
      return this.axios({
        url: '/casino/api/g/auth/session/',
        method: 'get',
        baseURL: '',
      })
    },
    setSession: async (data) => {
      return this.axios({
        url: '/casino/api/g/auth/session/bk/',
        method: 'post',
        baseURL: '',
        data,
      })
    },
    removeSession: async () => {
      return this.axios({
        url: '/casino/api/g/auth/session/',
        method: 'delete',
        baseURL: '',
      })
    },
    twoAuth: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/2fa/check_code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
  }

  autologin = {
    getAppToken: async () => {
      return this.axios({
        url: AUTOLOGIN_ENDPOINTS.appToken,
        method: 'post',
        baseURL: '',
        timeout: AUTOLOGIN_APP_TOKEN_TIMEOUT,
      })
    },
    validate: async (token) => {
      return this.axios({
        url: AUTOLOGIN_ENDPOINTS.validate,
        method: 'post',
        data: { token },
        baseURL: '',
        headers: {
          Authorization: null,
        },
      })
    },
  }

  parse = {
    sportbook: async (locale = 'en') => {
      return this.axios({
        url: '/parse',
        baseURL: window.location.origin,
        method: 'post',
        timeout: 60 * 1000,
        data: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale,
          platform: process.env.PLATFORM,
        },
      })
    },
  }

  payments = {
    list: {
      topup: async (payload, options = {}) => {
        return this.axios.get('/paygate/method/topup/list/', payload, options)
      },
      withdraw: async (payload, options = {}) => {
        return this.axios.get('/paygate/method/withdraw/list/', payload, options)
      },
    },
    create: {
      topup: (payload, config = {}) => this.axios.post('/paygate/order/topup/create/', payload, config),
      withdraw: (payload, config = {}) => this.axios.post('/paygate/order/withdraw/create/', payload, config),
    },
    form: {
      topup: (payload) => this.axios.get('/paygate/order/topup/init/', payload),
      withdraw: (payload) => this.axios.get('/paygate/order/withdraw/init/', payload),
    },
    sms: {
      channels: (payload) => this.axios.get('/paygate/order/withdraw/channels/', payload),
      getCode: (payload) => this.axios.post('/paygate/order/withdraw/code/', payload),
    },
    init: {
      cancelRequest: () => {
        if (controller) {
          controller.abort()
          controller = null
        }
      },
      topup: (payload) => {
        controller = new AbortController()
        return this.axios.post('/paygate/order/topup/init/', payload, { signal: controller.signal })
      },
      next: (payload) => this.axios.post('/paygate/order/topup/next/', payload),
      withdraw: (payload) => this.axios.post('/paygate/order/withdraw/init/', payload),
      getUrl: (payload) => this.axios.get('/paygate/order/topup/url/', payload),
      confirm: (payload) => this.axios.post('/paygate/order/topup/confirm/', payload),
    },
    history: {
      all: (payload) => this.axios.get(config.api.transactions, payload),
      topup: (payload) => this.axios.get('/paygate/order/topup/list/', payload),
      withdraw: (payload) => this.axios.get('/paygate/order/withdraw/list/', payload),
      refund: (payload) => this.axios.post('/paygate/order/withdraw/refund/', payload),
    },
    code: {
      channels: (payload) => this.axios.get('/paygate/code/channels/', payload),
      get: (payload) => this.axios.post('/paygate/code/get/', payload),
      disable: (payload) => this.axios.post('/paygate/code/disable/', payload),
      enable: (payload) => this.axios.post('/paygate/code/enable/', payload),
    },
  }

  statistic = {
    send: async (payload) => {
      return this.axios({
        url: '/api_dwh/v1/provider1/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
  }

  dwh = {
    sendEvent: (eventName, region = '', device = 'UNDEFINED', params = {}) => {
      return this.axios({
        url: '/dwh-proxy/',
        method: 'post',
        adapter: ['fetch', 'xhr'],
        fetchOptions: {
          keepalive: true,
        },
        data: {
          events: [
            {
              timestamp: Date.now(),
              geo: region,
              eventName: eventName,
              ...params,
            },
          ],
          dataSource: {
            platform: 'CASINO',
            origin: device,
          },
        },
      })
    },
  }

  withdrawRequests = {
    get: async () => {
      return this.axios({
        url: '/paygate/order/withdraw/active/',
        method: 'get',
      })
    },
  }

  iosMirrors = {
    getDomain: () => {
      return this.axios({
        url: '/mirrors/domain-ios/',
        method: 'get',
      })
    },
  }
}
